/**
 * Keeps the affiliate portal in sync across browser tabs, mirroring
 * `useCrossTabSession` for the member session.
 *
 * Scoped to the affiliate storage key only, so a member signing out in another
 * tab never disturbs the affiliate session and vice versa.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { STORAGE_KEYS } from '@wlb/shared/constants';
import { AFFILIATE_ROUTES } from './affiliateRoutes';

export function useAffiliateCrossTabSession(): void {
  const { isSignedIn, signOut } = useAffiliateSession();
  const navigate = useNavigate();

  useEffect(() => {
    const onStorage = async (event: StorageEvent) => {
      if (event.key !== STORAGE_KEYS.AFFILIATE_SESSION) {
        return;
      }
      if (!event.newValue && isSignedIn) {
        navigate(AFFILIATE_ROUTES.LANDING, { replace: true });
        await signOut();
        return;
      }
      if (event.newValue && !isSignedIn) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isSignedIn, signOut, navigate]);
}
